/**
 * @file Patient-profile notes API (healthcare patient_notes endpoints — ST1).
 * @module api/patientNotesApi
 *
 * Replaces the old patient_info.notes JSON blob (auth) with first-class healthcare notes.
 */

import { ENV } from '../../config/runtimeEnv';

import { PatientNote } from "../../types/healthcare/PatientNotes";
import type { UserUuid } from "../../types/identity";
import { http } from "../../lib/http/client";

const HEALTHCARE_BASE_URL = ENV.VITE_HEALTHCARE_SERVER;

export interface PatientNotesFilters {
	q?: string;
	category?: string;
	tag?: string;
}

export interface CreatePatientNotePayload {
	patient_id: UserUuid;
	author_id: UserUuid;
	author_role?: string;
	body: string;
	category?: string | null;
	tags?: string[] | null;
	is_ai_assisted?: boolean;
	ai_status?: string | null;
}

export interface UpdatePatientNotePayload {
	actor_id: UserUuid;
	actor_role?: string;
	body?: string;
	category?: string | null;
	tags?: string[] | null;
}

export interface NoteActor {
	actor_id: UserUuid;
	actor_role?: string;
}

/** List a patient's notes, optionally filtered by search term / category / tag. */
export const fetchPatientNotes = async (patientId: UserUuid, filters: PatientNotesFilters): Promise<PatientNote[]> => {
	const response = await http.get(`${HEALTHCARE_BASE_URL}/patients/${patientId}/notes`, {
		params: filters,
	});

	return response.data;
};

/** Create a new note. */
export const createPatientNote = async (payload: CreatePatientNotePayload): Promise<PatientNote> => {
	const response = await http.post(`${HEALTHCARE_BASE_URL}/patients/notes`, payload);

	return response.data;
};

/** Update an existing note (role-guarded server-side). */
export const updatePatientNote = async (id: number | string, payload: UpdatePatientNotePayload): Promise<PatientNote> => {
	const response = await http.put(`${HEALTHCARE_BASE_URL}/patients/notes/${id}`, payload);

	return response.data;
};

/** Soft-delete a note (role-guarded server-side). Actor/company travel as query params. */
export const deletePatientNote = async (id: number | string, actor: NoteActor): Promise<void> => {
	await http.delete(`${HEALTHCARE_BASE_URL}/patients/notes/${id}`, {
		params: actor,
	});
};
